// filepath: apps/web/src/features/admin/templates/registry/PreVettedBadge.tsx
//
// FR-UI-ADM-11 AC-5 -- informational badge for rows carrying
// `pre_vetted=true AND publisher_kind='platform'`.
//
// Per FR-UI-CORE-9 AC-2: color/icon MUST NOT be the sole carrier.
// The badge carries `data-pre-vetted="true"`, an icon (⚡), and
// translated text "auto-activates on provisioning" sourced from
// FR-TPL-5 AC-2.
//
// SCOPE: this badge is INFORMATIONAL ONLY. The auto-promotion
// carve-out fires at FR-TEN-5 provisioning time, NOT in this
// install path. A manual install via this page still requires
// the normal human-review gate (FR-PROM-3) before promotion.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { TemplateRegistryRow } from "./types";

interface PreVettedBadgeProps {
  row: TemplateRegistryRow;
}

export function PreVettedBadge({
  row,
}: PreVettedBadgeProps): React.ReactElement | null {
  const { t } = useTranslation();

  // The DB CHECK constraint guarantees the
  // `pre_vetted=true AND publisher_kind='tenant'` combination
  // never appears (registry/types.go + DB `(NOT pre_vetted)
  // OR (publisher_kind = 'platform')`). We render no badge
  // when either condition fails.
  if (!row.pre_vetted || row.publisher_kind !== "platform") {
    return null;
  }

  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
      data-pre-vetted="true"
      data-testid="template-registry-pre-vetted-badge"
      title={t("admin.templates.registry.badge.tooltip")}
      aria-label={t("admin.templates.registry.badge.auto_activates")}
    >
      <span aria-hidden="true">⚡</span>
      <span>{t("admin.templates.registry.badge.auto_activates")}</span>
    </span>
  );
}
